import { useEffect, useState } from "react";

import { api, type CharacterData } from "../lib/api";
import { resolveBackendAssetUrl } from "../lib/backendAsset";
import { logError } from "../lib/errors";
import { getByPath, pickStringByPaths } from "../lib/path";
import { storage } from "../lib/storage";

export const DEFAULT_CHARACTER_AVATAR_SRC = "/img/no-image.png";

function normalizeId(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function resolveCurrentCharacterId(user: unknown): string | null {
  return normalizeId(
    getByPath(user, "currentCharacter.characterId") ??
      getByPath(user, "currentCharacter.character_id") ??
      getByPath(user, "data.currentCharacter.characterId") ??
      getByPath(user, "data.currentCharacter.character_id") ??
      getByPath(user, "currentCharacter.id") ??
      getByPath(user, "data.currentCharacter.id") ??
      getByPath(user, "currentCharacterId") ??
      getByPath(user, "data.currentCharacterId")
  );
}

function resolveAvatarSrcFromUser(user: unknown): string | undefined {
  const raw = pickStringByPaths(user, [
    "currentCharacter.iconImageUrl",
    "data.currentCharacter.iconImageUrl",
    "currentCharacter.iconImage",
    "data.currentCharacter.iconImage",
    "currentCharacter.imageUrl",
    "data.currentCharacter.imageUrl",
    "currentCharacter.image",
    "data.currentCharacter.image",
    "iconImageUrl",
    "data.iconImageUrl",
    "iconImage",
    "data.iconImage",
    "imageUrl",
    "data.imageUrl",
    "image",
    "data.image",
  ]);

  return resolveBackendAssetUrl(raw);
}

function resolveAvatarSrcFromCharacter(character: CharacterData | null): string | undefined {
  if (!character) return undefined;
  return resolveBackendAssetUrl(
    character.iconImageUrl ?? character.iconImage ?? character.imageUrl ?? character.image
  );
}

function findCurrentCharacter(user: unknown, characters: CharacterData[], fallbackCharacterId?: string) {
  const currentCharacterId = resolveCurrentCharacterId(user) ?? fallbackCharacterId ?? null;
  const currentCharacterName = pickStringByPaths(user, [
    "currentCharacter.characterName",
    "data.currentCharacter.characterName",
    "currentCharacter.name",
    "data.currentCharacter.name",
    "characterName",
    "data.characterName",
  ]);

  return (
    characters.find((character) => normalizeId(character.characterId ?? character.id) === currentCharacterId) ??
    characters.find((character) => character.isCurrent === true) ??
    characters.find((character) => {
      const name = character.characterName ?? character.name;
      return !!currentCharacterName && name === currentCharacterName;
    }) ??
    null
  );
}

export function useCurrentCharacterAvatar() {
  const [avatarSrc, setAvatarSrc] = useState(DEFAULT_CHARACTER_AVATAR_SRC);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const user = await api.getUser();
        const directAvatarSrc = resolveAvatarSrcFromUser(user);
        if (directAvatarSrc) {
          if (active) setAvatarSrc(directAvatarSrc);
          return;
        }

        const characters = await api.getCharacterList();
        const currentCharacter = findCurrentCharacter(user, characters, storage.getAuth().currentCharacterId);
        const listAvatarSrc = resolveAvatarSrcFromCharacter(currentCharacter);
        if (active) setAvatarSrc(listAvatarSrc ?? DEFAULT_CHARACTER_AVATAR_SRC);
      } catch (error) {
        logError(error);
        if (active) setAvatarSrc(DEFAULT_CHARACTER_AVATAR_SRC);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return avatarSrc;
}
